import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Clock, Search, Trash2, ExternalLink, Pause, Play, Zap, ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle, SkipForward } from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/utils';

const KIND_OPTIONS = [
  { value: 'all',           label: 'All kinds' },
  { value: 'promote',       label: 'Promote' },
  { value: 'karma_farm',    label: 'Karma farm' },
  { value: 'boost_comment', label: 'Boost comment' },
  { value: 'reply_mention', label: 'Reply mention' },
  { value: 'warmup',        label: 'Warmup' },
  { value: 'natural',       label: 'Natural browse' },
];

export default function Schedules() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [kind, setKind] = React.useState('all');
  const [enabledOnly, setEnabledOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const params = React.useMemo(() => {
    const p = {};
    if (kind !== 'all') p.task_kind = kind;
    if (enabledOnly) p.enabled_only = true;
    return p;
  }, [kind, enabledOnly]);

  const { data, isLoading } = useQuery({
    queryKey: ['org-schedules', params],
    queryFn: () => api.get('/schedules', { params }).then(r => r.data),
    refetchInterval: 20000,
  });

  const patchMut = useMutation({
    mutationFn: ({ agent_id, id, body }) => api.patch(`/agents/${agent_id}/schedules/${id}`, body).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-schedules'] }),
    onError: (e) => toast.error(e?.response?.data?.detail || 'Update failed'),
  });
  const deleteMut = useMutation({
    mutationFn: ({ agent_id, id }) => api.delete(`/agents/${agent_id}/schedules/${id}`).then(r => r.data),
    onSuccess: () => { toast.success('Schedule removed'); qc.invalidateQueries({ queryKey: ['org-schedules'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  });
  const triggerMut = useMutation({
    mutationFn: ({ agent_id, id }) => api.post(`/agents/${agent_id}/schedules/${id}/trigger`).then(r => r.data),
    onSuccess: (data, vars) => {
      toast.success('Fire queued — refreshing history in ~10s');
      // Pre-warm the history cache for this row
      qc.invalidateQueries({ queryKey: ['schedule-history', vars.agent_id, vars.id] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ['schedule-history', vars.agent_id, vars.id] }), 8000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['org-schedules'] }), 10000);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Trigger failed'),
  });

  const [expanded, setExpanded] = React.useState(() => new Set());
  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const items = React.useMemo(() => {
    let arr = data?.items || [];
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(s =>
        s.name.toLowerCase().includes(q)
        || s.agent_name.toLowerCase().includes(q)
        || s.cron_expr.includes(q)
        || (s.instruction || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [data, search]);

  const total = data?.total ?? 0;
  const enabledCount = data?.enabled_count ?? 0;
  const pausedCount = total - enabledCount;

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <PageHeader
        title="Recurring schedules"
        description={
          <>
            Every Temporal Schedule across your fleet — toggle, audit, and prune from one place.
            <span className="ml-2 rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] text-[hsl(var(--text-3))]">
              ⚡ Fire now
            </span>
            <span className="ml-2 text-[hsl(var(--text-3))]">— click the row to expand fire history (skipped/queued/completed) and next fire times.</span>
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Total schedules" value={total} testid="stat-total" />
        <Stat label="Active" value={enabledCount} accent="primary" testid="stat-active" />
        <Stat label="Paused" value={pausedCount} accent="muted" testid="stat-paused" />
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="flex flex-col gap-3 border-b border-white/5 p-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-[hsl(var(--text-3))]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, agent, cron, or instruction…"
              className="pl-8"
              data-testid="schedules-search"
            />
          </div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[180px]" data-testid="schedules-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-[hsl(var(--text-3))]">
            <Switch
              checked={enabledOnly}
              onCheckedChange={setEnabledOnly}
              data-testid="schedules-enabled-only"
            />
            Active only
          </label>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-xs text-[hsl(var(--text-3))]">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title={total === 0 ? 'No schedules yet' : 'No matches'}
            description={total === 0
              ? 'Create one from any agent\'s Schedule tab, or ask the Master Agent: "karma-farm every 3h on reddit-karma-04".'
              : 'Try clearing the filter or search.'}
            testId="schedules-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="schedules-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[28px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Cron</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Last fired</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(s => (
                  <React.Fragment key={s.id}>
                    <TableRow data-testid={`schedule-row-${s.id}`} className="hover:bg-[hsl(var(--surface-2))/0.5]">
                      <TableCell className="px-2">
                        <button
                          onClick={() => toggleExpand(s.id)}
                          className="flex h-6 w-6 items-center justify-center rounded text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text-1))]"
                          aria-label={expanded.has(s.id) ? 'Collapse history' : 'Expand history'}
                          data-testid={`schedule-expand-${s.id}`}
                        >
                          {expanded.has(s.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium">{s.name}</span>
                          {s.instruction && (
                            <span className="line-clamp-1 max-w-[280px] text-[11px] text-[hsl(var(--text-3))]">{s.instruction}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => nav(`/agents/${s.agent_id}`)}
                          className="inline-flex items-center gap-1 text-sm text-[hsl(var(--text-2))] hover:text-[hsl(var(--primary))]"
                          data-testid={`schedule-agent-link-${s.id}`}
                        >
                          {s.agent_name}
                          <ExternalLink className="h-3 w-3 opacity-60" />
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
                          {s.task_kind}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-[hsl(var(--text-2))]">{s.cron_expr}</TableCell>
                      <TableCell className="text-xs text-[hsl(var(--text-3))]">
                        {s.active_hours_start}h–{s.active_hours_end}h · {s.timezone.split('/').pop()} · ±{s.jitter_minutes}m
                      </TableCell>
                      <TableCell className="text-xs text-[hsl(var(--text-3))]">
                        <Clock className="mr-1 inline h-3 w-3" />
                        {s.last_fired_at ? relativeTime(s.last_fired_at) : 'never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={!s.enabled || (triggerMut.isPending && triggerMut.variables?.id === s.id)}
                            onClick={() => triggerMut.mutate({ agent_id: s.agent_id, id: s.id })}
                            data-testid={`schedule-fire-${s.id}`}
                            aria-label="Fire now"
                            title={s.enabled ? 'Fire now (manual trigger)' : 'Resume the schedule first'}
                          >
                            {triggerMut.isPending && triggerMut.variables?.id === s.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Zap className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                          </Button>
                          <Switch
                            checked={s.enabled}
                            onCheckedChange={(v) => patchMut.mutate({ agent_id: s.agent_id, id: s.id, body: { enabled: v } })}
                            data-testid={`schedule-toggle-${s.id}`}
                            aria-label={s.enabled ? 'Pause schedule' : 'Resume schedule'}
                          />
                          {s.enabled ? <Play className="h-3 w-3 text-[hsl(var(--primary))]" /> : <Pause className="h-3 w-3 text-[hsl(var(--text-3))]" />}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              if (window.confirm(`Delete schedule "${s.name}"? This can't be undone.`)) {
                                deleteMut.mutate({ agent_id: s.agent_id, id: s.id });
                              }
                            }}
                            data-testid={`schedule-delete-${s.id}`}
                            aria-label="Delete schedule"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded.has(s.id) && (
                      <TableRow data-testid={`schedule-history-row-${s.id}`}>
                        <TableCell colSpan={8} className="bg-[hsl(var(--surface-2))/0.4] p-0">
                          <ScheduleHistory agentId={s.agent_id} scheduleId={s.id} onOpenTask={(tid) => nav(`/tasks/${tid}`)} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'default', testid }) {
  const accentClass = accent === 'primary'
    ? 'text-[hsl(var(--primary))]'
    : accent === 'muted'
      ? 'text-[hsl(var(--text-3))]'
      : 'text-[hsl(var(--text-1))]';
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${accentClass}`}>{value}</div>
    </div>
  );
}

function ScheduleHistory({ agentId, scheduleId, onOpenTask }) {
  const { data, isLoading } = useQuery({
    queryKey: ['schedule-history', agentId, scheduleId],
    queryFn: () => api.get(`/agents/${agentId}/schedules/${scheduleId}/history`).then(r => r.data),
    refetchInterval: 12000,
  });

  if (isLoading) {
    return <div className="px-6 py-4 text-xs text-[hsl(var(--text-3))]">Loading fire history…</div>;
  }
  const fires = data?.recent_fires || [];
  const next = data?.next_fires || [];
  const ts = data?.temporal_state || {};

  return (
    <div className="grid gap-4 px-6 py-4 lg:grid-cols-[2fr_1fr]" data-testid={`schedule-history-${scheduleId}`}>
      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">
          Recent fires
          <span className="rounded border border-white/10 bg-[hsl(var(--surface-3))] px-1.5 py-0.5 font-mono text-[10px] normal-case">
            total: {ts.num_actions ?? '?'}
          </span>
          {ts.missed_catchup > 0 && (
            <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] text-amber-400 normal-case">
              missed catchup: {ts.missed_catchup}
            </span>
          )}
        </div>
        {fires.length === 0 ? (
          <div className="rounded border border-dashed border-white/10 bg-[hsl(var(--surface-1))] p-3 text-xs text-[hsl(var(--text-3))]">
            No fires recorded yet. {next[0] ? `Next: ${new Date(next[0]).toLocaleString()}.` : ''} Hit the ⚡ button to test now.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {fires.map((f, i) => (
              <FireRow key={i} fire={f} onOpenTask={onOpenTask} />
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-2 text-[11px] uppercase tracking-wider text-[hsl(var(--text-3))]">Next 5 fires</div>
        {next.length === 0 ? (
          <div className="text-xs text-[hsl(var(--text-3))]">No upcoming fires (paused or expired).</div>
        ) : (
          <ul className="space-y-1 font-mono text-[11px] text-[hsl(var(--text-2))]">
            {next.map((t, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <Clock className="h-3 w-3 opacity-60" /> {new Date(t).toLocaleString()}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FireRow({ fire, onOpenTask }) {
  const skipped = !!fire.skipped;
  const status = fire.task_status;
  const Icon = skipped
    ? SkipForward
    : status === 'completed' ? CheckCircle2
      : status === 'failed' ? XCircle
        : Loader2;
  const tone = skipped
    ? 'text-amber-400'
    : status === 'completed' ? 'text-[hsl(var(--primary))]'
      : status === 'failed' ? 'text-red-400'
        : 'text-[hsl(var(--text-3))]';
  return (
    <li
      className="flex flex-wrap items-center gap-2 rounded border border-white/5 bg-[hsl(var(--surface-1))] px-3 py-2 text-xs"
      data-testid={`fire-row-${fire.fired_at}`}
    >
      <Icon className={`h-3.5 w-3.5 ${tone} ${(!skipped && !status) ? 'animate-spin' : ''}`} />
      <span className="font-mono text-[11px] text-[hsl(var(--text-2))]">
        {fire.fired_at ? new Date(fire.fired_at).toLocaleString() : '—'}
      </span>
      {skipped ? (
        <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] text-amber-400">
          skipped: {fire.skip_reason || 'unknown'}
        </span>
      ) : fire.task_id ? (
        <>
          <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
            {status || 'queued'}
          </span>
          {fire.task_verdict && (
            <span className="font-mono text-[10px] text-[hsl(var(--text-3))]">verdict: {fire.task_verdict}</span>
          )}
          <button
            onClick={() => onOpenTask(fire.task_id)}
            className="ml-auto inline-flex items-center gap-1 rounded border border-white/10 px-2 py-0.5 font-mono text-[10px] text-[hsl(var(--text-2))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
            data-testid={`open-task-${fire.task_id}`}
          >
            Open task <ExternalLink className="h-3 w-3" />
          </button>
        </>
      ) : (
        <span className="font-mono text-[10px] text-[hsl(var(--text-3))]">workflow fired (no task linked yet)</span>
      )}
    </li>
  );
}
